import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/phones/SignaturePad";
import { api, errMsg } from "@/lib/api";

const base = `${process.env.REACT_APP_BACKEND_URL}/api/public/forms`;

export default function PublicFormPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/public/forms/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e))); }, [token]);
  useEffect(() => { load(); }, [load]);
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/public/forms/${token}/sign`, { signature: sig }); load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  if (error && !info) return <div className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center text-slate-600" data-testid="public-form-error">{error}</div>;
  if (!info) return <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-400">Caricamento...</div>;
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-lg space-y-4">
        <div className="flex items-center gap-3"><div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-900"><FileSignature className="h-5 w-5 text-white" /></div>
          <div><p className="text-lg font-bold" data-testid="public-form-title">{info.template_name}</p><p className="text-sm text-slate-500">{info.driver_name}</p></div></div>
        <p className="rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed" data-testid="public-form-text">{info.text}</p>
        {info.photos.map((p, i) => p.has && (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-3"><p className="mb-2 text-xs font-semibold text-slate-600">{p.label}</p>
            <img src={`${base}/${token}/photos/${i}`} alt={p.label} className="w-full rounded-md" /></div>
        ))}
        {info.signed ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="public-form-signed">
            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-600" /><p className="font-semibold text-emerald-800">Modulo firmato. Grazie!</p>
            <a href={`${base}/${token}/pdf`} className="mt-3 inline-block"><Button variant="outline" data-testid="public-form-pdf"><Download className="mr-2 h-4 w-4" /> Scarica PDF</Button></a>
          </div>
        ) : (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold">Firma qui sotto col dito</p>
            <SignaturePad onChange={setSig} testId="public-form-signature" />
            {error && <p className="text-sm text-rose-600">{error}</p>}
            <Button className="w-full" onClick={sign} disabled={!sig || busy} data-testid="public-form-sign">{busy ? "Invio..." : "Firma il modulo"}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
